// 内部片（`_` 前缀）：顶栏拨号相关的常量与纯函数（号码校验/长度上限/脱敏回显还原/最近拨号排序）。
// 仅供 QuickDialBar 入口与 QuickDialBar/ 下的展示分片使用，入口不重导出本文件。

import { maskPhone } from '../../../utils/format';
import type { Cdr } from '../../../api/cdr';

/** 通话计时 mm:ss（顶栏胶囊与通话信息条共用口径） */
export function fmtMMSS(startedAt?: number, now?: number) {
  if (!startedAt || !now) return '00:00';
  const s = Math.max(0, Math.floor((now - startedAt) / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

// 顶栏拨号校验：手机号 / 座机 / 国际号码（00 前缀）。
// 刻意不用 PHONE_REG——其含「5-15 位裸数字」放行（用于 CRM/黑名单等宽松录入），
// 会让「111111111111」这类无意义号码通过；拨号框输入仅允许数字/*/#，故 + 号不可录入。
export const DIAL_REG = /^1[3-9]\d{9}$|^0\d{2,3}-?\d{7,8}$|^00\d{8,15}$/;

// 拨号号码最大长度：手机 11 / 座机 ≤12 / 国际 00+≤15=17 位，20 位留余量（超长必然非法）
export const DIAL_MAX_LEN = 20;

/**
 * 回拨脱敏解析：把「话后/历史回显在号码框里的脱敏号」还原为背后的真实号码。
 * 仅当输入框内容与某次回显真实号码的脱敏形态完全一致时才信任映射；
 * 坐席手动编辑/重新输入后内容不匹配即按当前输入原文呼出。
 * 配合 echoDialNumber 使用（脱敏展示 + 真实回拨），其它需要「回显可回拨号码」的入口可直接复用。
 */
export function resolveEchoReal(display: string, echoReal: string): string {
  const d = String(display ?? '').trim();
  return echoReal && d && d === maskPhone(echoReal) ? echoReal : d;
}

/** 最近拨号列表上限（去重后的号码条数） */
export const HISTORY_LIMIT = 10;

/**
 * 最近拨号号码列表：按「拨打时刻」倒序去重（同号只留最近一次）。
 * 不能沿用接口返回顺序——列表按 CDR 落库先后（id）排，而 CDR 在通话挂断时才写入
 * （转接出去的后置腿可能再晚数十秒），落库先后与拨打先后并不总是一致，
 * 直接照抄会让较早的号码排到刚拨的号码前面。
 * 拨打时刻取 start_time；取不到（呼出腿起止时间为空等）回落 end_time/created_at，
 * 使其仍按落库先后参与排序，不会整批沉到列表末尾。
 */
export function recentDialNumbers(list: Cdr[], limit = HISTORY_LIMIT): string[] {
  const dialAt = (c: Cdr) => {
    const t = Date.parse(String(c.start_time || c.end_time || c.created_at || ''));
    return Number.isNaN(t) ? 0 : t;
  };
  const rows = [...(list || [])].sort((a, b) => dialAt(b) - dialAt(a) || Number(b.id || 0) - Number(a.id || 0));
  const seen: string[] = [];
  for (const c of rows) {
    const n = String(c.callee || c.original_callee || '').trim();
    if (n && !seen.includes(n)) seen.push(n);
  }
  return seen.slice(0, limit);
}
